import { Injectable, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthTokenStorageService } from '../init/auth-token-storage.service';

@Injectable({
  providedIn: 'root',
})
export class VotingHubService implements OnDestroy {
  private connection: signalR.HubConnection | null = null;
  private readonly voteRegistered$ = new Subject<number>();

  /** Emite o ID da eleição sempre que um voto é registrado via SignalR. */
  readonly voteRegistered: Observable<number> = this.voteRegistered$.asObservable();

  constructor(private readonly tokenStorage: AuthTokenStorageService) {}

  /**
   * Conecta ao hub e entra no grupo da eleição.
   * Se já estiver conectado ao mesmo grupo, é um no-op.
   */
  async connect(electionId: number): Promise<void> {
    if (this.connection) {
      return;
    }

    const hubUrl = `${environment.smocipaApiBaseUrl}/hubs/cipa-voting`;
    const token = this.tokenStorage.getAccessToken();
    const urlWithToken = `${hubUrl}?cipa_access_token=${encodeURIComponent(token)}`;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(urlWithToken)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    this.connection.on('VoteRegistered', (id: number) => {
      this.voteRegistered$.next(id);
    });

    await this.connection.start();
    await this.connection.invoke('JoinElection', electionId);
  }

  /**
   * Sai do grupo da eleição e encerra a conexão.
   */
  async disconnect(electionId: number): Promise<void> {
    if (!this.connection) {
      return;
    }

    try {
      await this.connection.invoke('LeaveElection', electionId);
    } catch {
      // ignora falhas ao sair — a conexão será encerrada de qualquer forma
    }

    await this.connection.stop();
    this.connection = null;
  }

  ngOnDestroy(): void {
    this.connection?.stop();
    this.voteRegistered$.complete();
  }
}
